import { logGamma } from "@/lib/stats/special";
import { log10Big, scientificFromLog10 } from "@/lib/tool-kit/arithmetic/power";
import { BIG_ONE } from "@/lib/tool-kit/math/bigint";

/**
 * Factorials on BigInt, plus the gamma-function extension for numbers that are not whole.
 *
 * `factorial` multiplies the range 1…n by splitting it in half and recursing, so the two
 * halves of every product are about the same size; with schoolbook multiplication that
 * is roughly twice as fast as multiplying 1, 2, 3 … into a growing number, and it keeps
 * 10,000! well under a few milliseconds of arithmetic.
 */
export const MAX_FACTORIAL = 10_000;
export const MAX_GAMMA_ARGUMENT = 170;

function rangeProduct(lo: number, hi: number): bigint {
  if (lo > hi) {
    return BIG_ONE;
  }
  if (lo === hi) {
    return BigInt(lo);
  }
  if (hi - lo === 1) {
    return BigInt(lo) * BigInt(hi);
  }
  const mid = Math.floor((lo + hi) / 2);
  return rangeProduct(lo, mid) * rangeProduct(mid + 1, hi);
}

/** n! for a whole number n ≥ 0. */
export function factorial(n: number): bigint {
  return n < 2 ? BIG_ONE : rangeProduct(2, n);
}

/** n!! = n × (n−2) × (n−4) × … down to 2 or 1. By convention 0!! = 1. */
export function doubleFactorial(n: number): bigint {
  let result = BIG_ONE;
  for (let k = n; k > 1; k -= 2) {
    result *= BigInt(k);
  }
  return result;
}

/** The terms ⌊n/5⌋, ⌊n/25⌋, ⌊n/125⌋ … that add up to the trailing zeros of n!. */
export function trailingZeroTerms(n: number): number[] {
  const terms: number[] = [];
  for (let power = 5; power <= n; power *= 5) {
    terms.push(Math.floor(n / power));
  }
  return terms;
}

export function trailingZeros(n: number): number {
  return trailingZeroTerms(n).reduce((sum, term) => sum + term, 0);
}

/** Trailing zeros counted from the digits themselves — a check on Legendre's formula. */
export function trailingZerosOf(value: bigint): number {
  const digits = value.toString();
  let count = 0;
  while (digits.at(-1 - count) === "0") {
    count += 1;
  }
  return count;
}

/* ------------------------------------------------------------- Stirling */

export interface StirlingResult {
  /** log10 of √(2πn)(n/e)^n. */
  log10: number;
  mantissa: string;
  exponent: number;
  /** (Stirling − n!) / n!, as a fraction. */
  relativeError: number;
  /** log10 of the one-term-corrected approximation √(2πn)(n/e)^n (1 + 1/(12n)). */
  correctedLog10: number;
  correctedRelativeError: number;
}

const SIG = 8;

/** Stirling's approximation for n ≥ 1, compared with the exact n! (given as a BigInt). */
export function stirling(n: number, exact: bigint): StirlingResult {
  const lnApprox = 0.5 * Math.log(2 * Math.PI * n) + n * Math.log(n) - n;
  const lnExact = log10Big(exact) * Math.LN10;
  const lnCorrected = lnApprox + Math.log1p(1 / (12 * n));
  const parts = scientificFromLog10(lnApprox / Math.LN10, SIG);
  return {
    log10: lnApprox / Math.LN10,
    mantissa: parts.mantissa,
    exponent: parts.exponent,
    relativeError: Math.expm1(lnApprox - lnExact),
    correctedLog10: lnCorrected / Math.LN10,
    correctedRelativeError: Math.expm1(lnCorrected - lnExact),
  };
}

/* --------------------------------------------------------------- gamma */

/**
 * Γ(x) for a real x that is not zero or a negative whole number, from the Lanczos
 * log-gamma already in the statistics module and the reflection formula below zero.
 */
export function gamma(x: number): number {
  if (x > 0) {
    return Math.exp(logGamma(x));
  }
  return Math.PI / (Math.sin(Math.PI * x) * Math.exp(logGamma(1 - x)));
}

/** n! for a non-integer n, as Γ(n + 1); null where it overflows or is undefined. */
export function gammaFactorial(n: number): number | null {
  if (n + 1 > MAX_GAMMA_ARGUMENT || n < -MAX_GAMMA_ARGUMENT) {
    return null;
  }
  if (Number.isInteger(n)) {
    return null;
  }
  const value = gamma(n + 1);
  return Number.isFinite(value) ? value : null;
}
